import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { getReferralCookie, clearReferralCookie } from '../../lib/referralCookie.js';

export default function Signup() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromBanner = searchParams.get('email')?.trim() || '';

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState(emailFromBanner);
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [referralCode, setReferralCode] = useState(null);

  // Pick up the referral code set by /ref/:code (cookie). Read once at mount;
  // a later cookie change won't update mid-form, which is fine for this flow.
  useEffect(() => {
    setReferralCode(getReferralCookie());
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!agreed) {
      setError('Please agree to receive the newsletter to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { firstName, email, password };
      if (referralCode) payload.referralCode = referralCode;
      await signUp(payload);
      clearReferralCookie();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Could not create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-hxd-body flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-7">
          <Link to="/" className="block">
            <div className="flex w-full h-1 mb-6">
              <span className="bg-hxd-orange flex-[45]" />
              <span className="bg-white flex-[10]" />
              <span className="bg-black flex-[45]" />
            </div>
            <h1 className="font-display text-[20px] font-bold tracking-display-sm uppercase m-0">
              Become a Subscriber
            </h1>
          </Link>
          <p className="text-[13px] text-hxd-muted mt-1">
            HXD Morning Brief — Aviation &amp; AAM Intelligence
          </p>
        </div>

        {referralCode && (
          <div
            role="status"
            className="mb-3 text-[12px] text-[#15803d] bg-[#e7f6ec] border border-[#bbf7d0] rounded px-3 py-2"
          >
            You were referred by a friend — they&apos;ll get credit once you confirm.
          </div>
        )}

        <form onSubmit={onSubmit} className="hxd-card space-y-4" noValidate>
          {error && (
            <div
              role="alert"
              className="text-[13px] text-[#c0392b] bg-[#fdecea] border border-[#f5c6cb] rounded px-3 py-2"
            >
              {error}
            </div>
          )}
          <div>
            <label className="hxd-label" htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              className="hxd-input"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
            />
          </div>
          <div>
            <label className="hxd-label" htmlFor="signup-email">Email Address</label>
            <input
              id="signup-email"
              className="hxd-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
            <p className="text-[12px] text-hxd-subtle mt-1">
              You&apos;ll receive the HXD Morning Brief weekday mornings.
            </p>
          </div>
          <div>
            <label className="hxd-label" htmlFor="signup-password">Password</label>
            <input
              id="signup-password"
              className="hxd-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters, with a letter and a number"
              required
              minLength={8}
              maxLength={72}
            />
          </div>
          <label className="flex items-start gap-2 text-[12px] text-[#555] cursor-pointer leading-relaxed">
            <input
              type="checkbox"
              className="mt-0.5 shrink-0"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              I agree to receive the HXD Morning Brief newsletter. Unsubscribe anytime.
            </span>
          </label>
          <button
            type="submit"
            className="hxd-btn hxd-btn-primary w-full text-[15px] py-3"
            disabled={submitting}
          >
            {submitting ? 'Creating account…' : 'Create Account & Subscribe'}
          </button>
        </form>

        <p className="text-center text-[13px] text-[#666] mt-5">
          Already have an account?{' '}
          <Link to="/login" className="text-hxd-orange font-bold">
            Sign in →
          </Link>
        </p>
        <p className="text-center text-[12px] text-hxd-subtle mt-2">
          <Link to="/" className="hover:text-hxd-orange">
            ← Back to today&apos;s brief
          </Link>
        </p>
      </div>
    </div>
  );
}
